/**
 * LC (Learning Center) API
 *
 * Timeslot 관리 + Enrollment 관리
 */
import { apiClient, ApiResponse } from './client'
import type { ContentFeeGate, ContentFeeNotice } from '@/utils/contentFeeGate'

// ============================================================================
// Timeslot Types
// ============================================================================

export interface Timeslot {
  timeslotId: number
  organizationId: number
  name: string
  description: string | null
  schedule: string
  teacherUserId: number | null
  teacherName: string | null
  maxStudents: number | null  // null = 무제한 정원 (P0 T2)
  currentStudents: number
  onLeaveStudents: number
  isActive: boolean
  allowSelfGrading: boolean // Phase 4b (§5-7=A): 원생 빌드업 자가채점 허용
  createdAt: string
  updatedAt: string
}

/** 클래스 삭제 결과. DEACTIVATED = 무료체험 이력이 남아 하드 삭제 대신 비활성 처리됨. */
export type TimeslotDeleteOutcome = 'DELETED' | 'DEACTIVATED'

export interface TimeslotDeleteResponse {
  timeslotId: number
  outcome: TimeslotDeleteOutcome
}

export interface TimeslotCreateRequest {
  name: string
  description?: string
  schedule?: string
  teacherUserId?: number
  maxStudents?: number
  allowSelfGrading?: boolean
}

export interface TimeslotUpdateRequest {
  name?: string
  description?: string
  schedule?: string
  teacherUserId?: number
  maxStudents?: number
  isActive?: boolean
  allowSelfGrading?: boolean
}

// ============================================================================
// Timeslot Closure (휴강) Types — P2
// ============================================================================

export type TimeslotClosureState = 'SCHEDULED' | 'ONGOING' | 'ENDED'

export interface TimeslotClosure {
  closureId: number
  timeslotId: number
  organizationId: number
  startDate: string
  endDate: string
  reason: string | null
  state: TimeslotClosureState // 조회일(KST) 기준 파생: 예정/휴강중/종료
  createdAt: string
  updatedAt: string
}

export interface TimeslotClosureCreateRequest {
  timeslotId: number
  startDate: string
  endDate: string
  reason?: string
}

export interface TimeslotClosureUpdateRequest {
  startDate?: string
  endDate?: string
  reason?: string
}

// ============================================================================
// Assignable Student Types — P3 (원생 배정)
// ============================================================================

export interface AssignableStudent {
  userId: number
  fullName: string
  handle: string
  email: string
  status: string // WAITING — 퇴원 원생은 전용 재입회 흐름 사용
  gradeLevel: string | null
  gender: string | null   // raw enum name (lowercase); FE labels it
  guardianPhone: string | null
  lcEnrollmentId?: number | null  // 클래스 미배정(active+timeslot=null) 수강 PK — 있으면 재배정 후보: create 대신 update({timeslotId})
  productName?: string | null     // 재배정 후보의 기존 수강 과정명 (학습과정 고정 표시용)
}

// P4 배정 원생 목록(roster) 1행. 상태=파생 LearnerEnrollmentStatus, endDate=수강 종료일(null=무기한).
export interface RosterStudent {
  lcEnrollmentId: number          // 이 클래스 enrollment PK (P5 액션 대상: 반이동/제외/과정변경)
  userId: number
  fullName: string
  handle: string
  email: string
  gradeLevel: string | null
  gender: string | null
  guardianPhone: string | null
  productName: string | null
  completionRate: number | null   // % (null=N/A)
  currentCurriculum: string | null
  status: string                  // WAITING|ENROLLED|ON_LEAVE|WITHDRAWN
  unusedLessons: number | null     // active content-fee remaining lessons; null=not configured
  learningEndDate: string | null   // active content-fee periodEnd
  contentFeeGate?: ContentFeeGate | null
  contentFeeNotice?: ContentFeeNotice | null // PERIOD_ENDED: 유예 중, PURCHASABLE: 직전 결제 21일 경과
  endDate: string | null          // ISO date; null=무기한
  trialId?: number | null
  trialSetCode?: 'A' | 'B' | 'C' | 'D' | 'E' | null
  trialStatus?: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CONVERTED' | 'REVOKED' | null
  trialPeriodStart?: string | null
  trialPeriodEnd?: string | null
  trialRemainingDays?: number | null
  timeslotId?: number | null
  timeslotName?: string | null
}

export interface RosterStudentPageResponse {
  content: RosterStudent[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

export interface RosterSearchParams {
  q?: string
  status?: 'ENROLLED' | 'ON_LEAVE'
  page?: number
  size?: number
}

// ============================================================================
// Enrollment Types
// ============================================================================

export interface LcEnrollment {
  lcEnrollmentId: number
  enrollmentId: number | null
  organizationId: number
  userId: number
  userName: string | null
  userEmail: string | null
  licenseId: number
  productId: number
  productName: string | null
  timeslotId: number | null
  timeslotName: string | null
  lessonConfiguration: string | null
  startDate: string | null
  endDate: string | null
  unusedLessons: number | null
  learningEndDate: string | null
  contentFeeGate?: ContentFeeGate | null
  contentFeeNotice?: ContentFeeNotice | null // PERIOD_ENDED: 유예 중, PURCHASABLE: 직전 결제 21일 경과
  status: string
  createdAt: string
  updatedAt: string
  trialId?: number | null
  trialSetCode?: 'A' | 'B' | 'C' | 'D' | 'E' | null
  trialStatus?: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CONVERTED' | 'REVOKED' | null
}

export interface LcEnrollmentCreateRequest {
  userId: number
  licenseId: number
  timeslotId?: number
  startDate?: string
  endDate?: string
}

export interface LcEnrollmentUpdateRequest {
  timeslotId?: number
  clearTimeslot?: boolean          // P5 축2=A: true → timeslotId=null(클래스 배정 해제 → 대기)
  startDate?: string
  endDate?: string
  status?: string
}

export interface LcEnrollmentConfigRequest {
  lessonConfiguration: string
}

// 과정 변경(A→B). targetTimeslotId 미지정 시 source timeslot 승계.
export interface LcCourseChangeRequest {
  targetLicenseId: number
  targetTimeslotId?: number | null
  reason: string
}

export interface LcCourseChangeResponse {
  previousEnrollment: LcEnrollment
  currentEnrollment: LcEnrollment
  cancelledTextbookRequestIds: number[]
  auditId: number
}

// ============================================================================
// Timeslot API
// ============================================================================

export const lcTimeslotApi = {
  getAll: (includeInactive = false) =>
    apiClient.get<ApiResponse<Timeslot[]>>('/lc/timeslots', {
      params: { includeInactive }
    }),

  getById: (id: number) =>
    apiClient.get<ApiResponse<Timeslot>>(`/lc/timeslots/${id}`),

  create: (data: TimeslotCreateRequest) =>
    apiClient.post<ApiResponse<Timeslot>>('/lc/timeslots', data),

  update: (id: number, data: TimeslotUpdateRequest) =>
    apiClient.put<ApiResponse<Timeslot>>(`/lc/timeslots/${id}`, data),

  delete: (id: number) =>
    apiClient.delete<ApiResponse<TimeslotDeleteResponse>>(`/lc/timeslots/${id}`),

  // P3: 배정가능 원생 목록(active/on_leave 미보유). 신규 배정 모달에서 사용.
  getAssignableStudents: (id: number) =>
    apiClient.get<ApiResponse<AssignableStudent[]>>(`/lc/timeslots/${id}/assignable-students`),

  // P4: 배정 원생 목록(roster — active/on_leave). 시간표 카드 [원생 목록]에서 사용.
  getRoster: (id: number) =>
    apiClient.get<ApiResponse<RosterStudent[]>>(`/lc/timeslots/${id}/roster`),

  searchRoster: (params: RosterSearchParams) =>
    apiClient.get<ApiResponse<RosterStudentPageResponse>>('/lc/timeslots/roster/search', {
      params: {
        q: params.q || undefined,
        status: params.status || undefined,
        page: params.page ?? 0,
        size: params.size ?? 20,
      },
    }),
}

// ============================================================================
// Timeslot Closure API (휴강 관리) — P2. base는 /lc/timeslot-closures (경로 충돌 회피).
// ============================================================================

export const lcTimeslotClosureApi = {
  list: (timeslotId?: number) =>
    apiClient.get<ApiResponse<TimeslotClosure[]>>('/lc/timeslot-closures', {
      params: timeslotId != null ? { timeslotId } : undefined,
    }),

  create: (data: TimeslotClosureCreateRequest) =>
    apiClient.post<ApiResponse<TimeslotClosure>>('/lc/timeslot-closures', data),

  update: (closureId: number, data: TimeslotClosureUpdateRequest) =>
    apiClient.put<ApiResponse<TimeslotClosure>>(`/lc/timeslot-closures/${closureId}`, data),

  delete: (closureId: number) =>
    apiClient.delete<ApiResponse<void>>(`/lc/timeslot-closures/${closureId}`),
}

// ============================================================================
// Enrollment API (Admin)
// ============================================================================

export const lcEnrollmentApi = {
  getAll: () =>
    apiClient.get<ApiResponse<LcEnrollment[]>>('/lc/enrollments'),

  getById: (id: number) =>
    apiClient.get<ApiResponse<LcEnrollment>>(`/lc/enrollments/${id}`),

  create: (data: LcEnrollmentCreateRequest) =>
    apiClient.post<ApiResponse<LcEnrollment>>('/lc/enrollments', data),

  update: (id: number, data: LcEnrollmentUpdateRequest) =>
    apiClient.put<ApiResponse<LcEnrollment>>(`/lc/enrollments/${id}`, data),

  updateConfiguration: (id: number, data: LcEnrollmentConfigRequest) =>
    apiClient.put<ApiResponse<LcEnrollment>>(`/lc/enrollments/${id}/configuration`, data),

  cancel: (id: number) =>
    apiClient.delete<ApiResponse<LcEnrollment>>(`/lc/enrollments/${id}`),

  // 과정 변경 — source 수강 취소 + target 수강 활성화를 서버가 한 트랜잭션으로 커밋한다.
  changeCourse: (sourceEnrollmentId: number, data: LcCourseChangeRequest) =>
    apiClient.post<ApiResponse<LcCourseChangeResponse>>(
      `/lc/enrollments/${sourceEnrollmentId}/change-course`,
      data,
    ),
}
